import { BodyText } from '@/components/BodyText';
import { HeaderText } from '@/components/HeaderText';
import { useFetchCrossTenantGeometricSearch } from '@/hooks/api/crossTenant/assetsGeometricSearch/useFetchCrossTenantGeometricSearch';
import { useFetchCrossTenantPartSearch } from '@/hooks/api/crossTenant/assetsPartSearch/useFetchCrossTenantPartSearch';
import { useFetchCrossTenantVisualSearch } from '@/hooks/api/crossTenant/assetsVisualSearch/useFetchCrossTenantVisualSearch';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { useSearchTabs } from '@/hooks/useSearchTabs';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { useTenants } from '@/services/tenants/useTenants';
import { Close } from '@mui/icons-material';
import { Box, Button, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { SearchType } from '../types';

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '16px',
});

const QueryPill = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '4px',
  padding: '0px 6px 0 8px',
  border: `1px solid ${theme.palette.divider}`,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontWeight: '500',
}));

const CloseButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 0,
  margin: 0,
  flexShrink: 0,
  width: 'unset',
  minWidth: 'unset',
  '&:hover': {
    opacity: 0.7,
  },
});

const CloseIcon = styled(Close)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
  pointerEvents: 'none',
}));

export const NoSearchResults = () => {
  const { searchType, selectedFilters, minThreshold, sizeThreshold, assetId, searchQuery, updateSearchQuery } =
    useSearchFilters();
  const { isTrial } = useTrialStatus();

  const activeThreshold = searchType === 'visual' ? sizeThreshold : minThreshold;

  const [hasResultsWithZeroThreshold, setHasResultsWithZeroThreshold] = useState<boolean | undefined>();
  const { getTenantId } = useTenants();
  const { selectedSearchTab } = useSearchTabs();
  const currentTenantId = getTenantId();
  const searchTenantId = selectedSearchTab?.tenantId ?? currentTenantId;

  const { fetchData: fetchCrossTenantGeometricSearchWithZeroThreshold } = useFetchCrossTenantGeometricSearch({
    assetId: assetId || '',
    assetTenantId: currentTenantId,
    searchTenantId,
    searchQuery,
    selectedFilters,
    minThreshold: 0,
  });

  const { fetchData: fetchCrossTenantPartSearchWithZeroThreshold } = useFetchCrossTenantPartSearch({
    assetId: assetId || '',
    assetTenantId: currentTenantId,
    searchTenantId,
    searchQuery,
    selectedFilters,
    minThreshold: 0,
  });

  const { fetchData: fetchCrossTenantVisualSearchWithZeroThreshold } = useFetchCrossTenantVisualSearch({
    assetId: assetId || '',
    assetTenantId: currentTenantId,
    searchTenantId,
    searchQuery,
    selectedFilters,
    sizeThreshold: 0,
  });

  useEffect(() => {
    const checkResultsWithZeroThreshold = async () => {
      if (
        (searchType === 'geometric' || searchType === 'part' || searchType === 'visual') &&
        assetId &&
        activeThreshold > 0
      ) {
        try {
          let response;
          switch (searchType) {
            case 'geometric': {
              response = await fetchCrossTenantGeometricSearchWithZeroThreshold();
              break;
            }
            case 'part': {
              response = await fetchCrossTenantPartSearchWithZeroThreshold();
              break;
            }
            case 'visual': {
              response = await fetchCrossTenantVisualSearchWithZeroThreshold();
              break;
            }
          }
          setHasResultsWithZeroThreshold((response?.pageData?.total ?? 0) > 0);
        } catch {
          setHasResultsWithZeroThreshold(false);
        }
      } else {
        setHasResultsWithZeroThreshold(undefined);
      }
    };

    checkResultsWithZeroThreshold();
  }, [
    searchType,
    assetId,
    activeThreshold,
    searchQuery,
    fetchCrossTenantGeometricSearchWithZeroThreshold,
    fetchCrossTenantPartSearchWithZeroThreshold,
    fetchCrossTenantVisualSearchWithZeroThreshold,
  ]);

  const shouldLowerThresholdForResults =
    hasResultsWithZeroThreshold === true &&
    (searchType === 'geometric' || searchType === 'part' || searchType === 'visual');

  const thresholdType = searchType === 'visual' ? 'Size Threshold' : 'Match Threshold';

  const handleClearQuery = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    updateSearchQuery('');
  };

  const showTrialDemoAssetsHint = !searchType && isTrial;

  return (
    <Container>
      <HeaderText>
        <NoResultsMessage searchType={searchType} searchQuery={searchQuery} onClearQuery={handleClearQuery} />
      </HeaderText>
      {shouldLowerThresholdForResults && (
        <BodyText color='text.secondary'>Setting a lower {thresholdType} will show results</BodyText>
      )}
      {showTrialDemoAssetsHint && (
        <BodyText color='text.secondary' sx={{ mt: 1 }}>
          Demo assets are not shown on this page. Check the Assets page to view all assets.
        </BodyText>
      )}
    </Container>
  );
};

const NoResultsMessage = ({
  searchType,
  searchQuery,
  onClearQuery,
}: {
  searchType: SearchType;
  searchQuery?: string;
  onClearQuery: (event: React.MouseEvent) => void;
}) => {
  if (searchQuery) {
    const searchTypeText = !searchType || searchType === 'text' ? '' : `${searchType} match`;
    return (
      <>
        No {searchTypeText} results found with text{' '}
        <QueryPill>
          {searchQuery}
          <CloseButton type='button' onMouseUp={onClearQuery}>
            <CloseIcon />
          </CloseButton>
        </QueryPill>
      </>
    );
  }

  if (!searchType) {
    return 'No results found';
  }
  const resultsLabel = searchType === 'substitutes' ? searchType : `${searchType} match results`;
  return `No ${resultsLabel} found`;
};
